import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  CircularProgress, 
  Paper,
  Avatar,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExtensionIcon from '@mui/icons-material/Extension';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import FolderIcon from '@mui/icons-material/Folder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import FilterListIcon from '@mui/icons-material/FilterList';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const summaryCards = [
  { 
    key: 'projects', 
    label: 'Total Projects', 
    icon: <DashboardIcon fontSize="large" />,
    color: '#3b82f6',
    bgColor: '#dbeafe',
    trend: '+12%',
    trendUp: true,
    unit: '',
    precision: 0
  },
  { 
    key: 'proposals', 
    label: 'Active Proposals', 
    icon: <DescriptionIcon fontSize="large" />,
    color: '#8b5cf6',
    bgColor: '#ede9fe',
    trend: '+8%',
    trendUp: true,
    unit: '',
    precision: 0
  },
  { 
    key: 'tasks', 
    label: 'Completed Tasks', 
    icon: <AssignmentIcon fontSize="large" />,
    color: '#10b981',
    bgColor: '#d1fae5',
    trend: '+15%',
    trendUp: true,
    unit: '',
    precision: 0
  },
  { 
    key: 'features', 
    label: 'Features', 
    icon: <ExtensionIcon fontSize="large" />,
    color: '#f59e0b',
    bgColor: '#fef3c7',
    trend: '-3%',
    trendUp: false,
    unit: '',
    precision: 0
  },
  { 
    key: 'technologies', 
    label: 'Technologies', 
    icon: <GroupWorkIcon fontSize="large" />,
    color: '#06b6d4',
    bgColor: '#cffafe',
    trend: '+5%',
    trendUp: true,
    unit: '',
    precision: 0
  },
  { 
    key: 'case_studies', 
    label: 'Case Studies', 
    icon: <FolderIcon fontSize="large" />,
    color: '#ec4899',
    bgColor: '#fce7f3',
    trend: '+20%',
    trendUp: true,
    unit: '',
    precision: 0
  },
];

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];

// Default chart data to prevent null errors
const defaultChartData = [
  { name: 'Jan', projects: 4, proposals: 3, tasks: 12 },
  { name: 'Feb', projects: 6, proposals: 5, tasks: 18 },
  { name: 'Mar', projects: 8, proposals: 7, tasks: 25 },
  { name: 'Apr', projects: 12, proposals: 10, tasks: 32 },
  { name: 'May', projects: 16, proposals: 14, tasks: 45 },
  { name: 'Jun', projects: 24, proposals: 18, tasks: 56 },
];

const defaultPieData = [
  { name: 'Completed', value: 156 },
  { name: 'In Progress', value: 89 },
  { name: 'Pending', value: 18 },
  { name: 'Planning', value: 24 },
];

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animatedValues, setAnimatedValues] = useState({});
  const [chartType, setChartType] = useState('bar');
  const [timeRange, setTimeRange] = useState('6M');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [realTimeData, setRealTimeData] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [chartData, setChartData] = useState(defaultChartData);
  const [pieData, setPieData] = useState(defaultPieData);
  const [recentActivities, setRecentActivities] = useState([]);
  const [projectProgress, setProjectProgress] = useState([]);
  const [error, setError] = useState(null);

  // API Base URL
  const API_BASE_URL = 'https://sop.magento2extensions.com/api';

  // Fetch dashboard summary data
  const fetchDashboardSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/summary`);
      if (!response.ok) throw new Error('Failed to fetch dashboard summary');
      const data = await response.json();
      setSummary(data);
      return data;
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      setError('Failed to load dashboard summary');
      return null;
    }
  }, []);

  // Fetch analytics chart data
  const fetchAnalyticsData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/analytics`);
      if (!response.ok) throw new Error('Failed to fetch analytics data');
      const data = await response.json();
      setChartData(data);
      return data;
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setChartData(defaultChartData);
      return defaultChartData;
    }
  }, []);

  // Fetch pie chart data
  const fetchPieData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/pie-data`);
      if (!response.ok) throw new Error('Failed to fetch pie data');
      const data = await response.json();
      setPieData(data);
      return data;
    } catch (error) {
      console.error('Error fetching pie data:', error);
      setPieData(defaultPieData);
      return defaultPieData;
    }
  }, []);

  // Fetch recent activities
  const fetchRecentActivities = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/activities`);
      if (!response.ok) throw new Error('Failed to fetch activities');
      const data = await response.json();
      setRecentActivities(data);
      return data;
    } catch (error) {
      console.error('Error fetching activities:', error);
      const defaultActivities = [
        { 
          id: 1, 
          action: 'New proposal created', 
          user: 'John Doe', 
          time: '2 hours ago', 
          type: 'proposal',
          priority: 'high',
          status: 'active'
        },
        { 
          id: 2, 
          action: 'Task completed', 
          user: 'Jane Smith', 
          time: '4 hours ago', 
          type: 'task',
          priority: 'medium',
          status: 'completed'
        },
        { 
          id: 3, 
          action: 'Feature added', 
          user: 'Mike Johnson', 
          time: '6 hours ago', 
          type: 'feature',
          priority: 'low',
          status: 'active'
        },
        { 
          id: 4, 
          action: 'Document uploaded', 
          user: 'Sarah Wilson', 
          time: '1 day ago', 
          type: 'document',
          priority: 'medium',
          status: 'pending'
        },
      ];
      setRecentActivities(defaultActivities);
      return defaultActivities;
    }
  }, []);

  // Fetch project progress
  const fetchProjectProgress = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/project-progress`);
      if (!response.ok) throw new Error('Failed to fetch project progress');
      const data = await response.json();
      setProjectProgress(data);
      return data;
    } catch (error) {
      console.error('Error fetching project progress:', error);
      const defaultProgress = [
        { id: 1, name: 'Project A', progress: 75, status: 'In Progress', team: 5, deadline: '2024-02-15' },
        { id: 2, name: 'Project B', progress: 45, status: 'Planning', team: 3, deadline: '2024-03-01' },
        { id: 3, name: 'Project C', progress: 90, status: 'Testing', team: 8, deadline: '2024-01-30' },
        { id: 4, name: 'Project D', progress: 30, status: 'Development', team: 6, deadline: '2024-04-15' },
      ];
      setProjectProgress(defaultProgress);
      return defaultProgress;
    }
  }, []);

  // Fetch real-time data
  const fetchRealTimeData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboard/realtime`);
      if (!response.ok) throw new Error('Failed to fetch real-time data');
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching real-time data:', error);
      return {
        time: new Date().toLocaleTimeString(),
        activeUsers: Math.floor(Math.random() * 50) + 20,
        recentTasks: Math.floor(Math.random() * 10) + 5,
        systemLoad: Math.floor(Math.random() * 30) + 40,
        category: ['A', 'B', 'C'][Math.floor(Math.random() * 3)]
      };
    }
  }, []);

  // Animated counter effect
  const animateValue = useCallback((start, end, duration, key) => {
    const startTime = performance.now();
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.floor(start + (end - start) * progress);
      
      setAnimatedValues(prev => ({
        ...prev,
        [key]: current
      }));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, []);

  // Initialize all data
  const initializeData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all data in parallel
      const [summaryData, analyticsData, pieData, activities, progress] = await Promise.all([
        fetchDashboardSummary(),
        fetchAnalyticsData(),
        fetchPieData(),
        fetchRecentActivities(),
        fetchProjectProgress()
      ]);

      // Animate counters if summary data is available
      if (summaryData) {
        Object.keys(summaryData).forEach(key => {
          animateValue(0, summaryData[key], 2000, key);
        });
      }

      setLoading(false);
      
      // Show welcome notification
      setTimeout(() => {
        setNotificationMessage('Dashboard loaded successfully with real data!');
        setShowNotification(true);
      }, 500);
      
    } catch (error) {
      console.error('Error initializing data:', error);
      setError('Failed to load dashboard data');
      setLoading(false);
    }
  }, [fetchDashboardSummary, fetchAnalyticsData, fetchPieData, fetchRecentActivities, fetchProjectProgress, animateValue]);

  // Auto-refresh functionality
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(async () => {
        const summaryData = await fetchDashboardSummary();
        if (summaryData) {
          // Update animated values with new data
          Object.keys(summaryData).forEach(key => {
            setAnimatedValues(prev => ({
              ...prev,
              [key]: summaryData[key]
            }));
          });
        }
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh, fetchDashboardSummary]);

  // Live data simulation
  useEffect(() => {
    if (isLive) {
      const interval = setInterval(async () => {
        const realTimeDataPoint = await fetchRealTimeData();
        setRealTimeData(prev => {
          const newData = [...prev];
          if (newData.length > 20) newData.shift();
          
          newData.push({
            time: realTimeDataPoint.time,
            value: realTimeDataPoint.activeUsers,
            category: realTimeDataPoint.category
          });
          
          return newData;
        });
      }, 2000); // Update every 2 seconds

      return () => clearInterval(interval);
    }
  }, [isLive, fetchRealTimeData]);

  // Initialize data on component mount
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  // Handle chart menu
  const handleChartMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChartMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle refresh
  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    
    try {
      await initializeData();
      setNotificationMessage('Data refreshed successfully from server!');
      setShowNotification(true);
    } catch (error) {
      setNotificationMessage('Failed to refresh data');
      setShowNotification(true);
    }
  };

  // Handle activity click
  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
    setNotificationMessage(`Viewing details for: ${activity.action}`);
    setShowNotification(true);
  };

  // Safe chart renderer
  const renderChart = () => {
    const data = chartData || defaultChartData;
    
    try {
      switch (chartType) {
        case 'bar':
          return (
            <BarChart data={data}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <RechartsTooltip 
                contentStyle={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Bar dataKey="projects" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="proposals" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="tasks" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          );
        case 'line':
          return (
            <LineChart data={data}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <RechartsTooltip 
                contentStyle={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="projects" stroke="#3b82f6" strokeWidth={3} />
              <Line type="monotone" dataKey="proposals" stroke="#8b5cf6" strokeWidth={3} />
              <Line type="monotone" dataKey="tasks" stroke="#10b981" strokeWidth={3} />
            </LineChart>
          );
        case 'area':
          return (
            <AreaChart data={data}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <RechartsTooltip 
                contentStyle={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Area type="monotone" dataKey="projects" fill="#3b82f6" stroke="#3b82f6" fillOpacity={0.3} />
              <Area type="monotone" dataKey="proposals" fill="#8b5cf6" stroke="#8b5cf6" fillOpacity={0.3} />
              <Area type="monotone" dataKey="tasks" fill="#10b981" stroke="#10b981" fillOpacity={0.3} />
            </AreaChart>
          );
        default:
          return (
            <BarChart data={data}>
              <XAxis dataKey="name" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <RechartsTooltip 
                contentStyle={{
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Legend />
              <Bar dataKey="projects" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="proposals" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="tasks" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          );
      }
    } catch (error) {
      console.error('Chart rendering error:', error);
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            Chart loading...
          </Typography>
        </Box>
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading dashboard from server...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={handleRefresh} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <Typography variant="h6" color="text.secondary">No data available.</Typography>
        <Button variant="contained" onClick={handleRefresh} sx={{ mt: 2 }}>
          Refresh
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%' }}>
      {/* Welcome Section with Controls */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
            Welcome back, Admin! 👋
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Here's what's happening with your projects today.
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch 
                checked={autoRefresh} 
                onChange={(e) => setAutoRefresh(e.target.checked)}
                color="primary"
              />
            }
            label="Auto Refresh"
          />
          <FormControlLabel
            control={
              <Switch 
                checked={isLive} 
                onChange={(e) => setIsLive(e.target.checked)}
                color="secondary"
              />
            }
            label="Live Data"
          />
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {summaryCards.map(card => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={card.key}>
            <Card sx={{ 
              height: '100%',
              background: 'white',
              borderRadius: 3,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #f1f5f9',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              }
            }}
            onClick={() => {
              setNotificationMessage(`Clicked on ${card.label}`);
              setShowNotification(true);
            }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: card.bgColor, 
                    color: card.color,
                    width: 48,
                    height: 48
                  }}>
                    {card.icon}
                  </Avatar>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {card.trendUp ? (
                      <TrendingUpIcon sx={{ color: 'success.main', fontSize: 16 }} />
                    ) : (
                      <TrendingDownIcon sx={{ color: 'error.main', fontSize: 16 }} />
                    )}
                    <Typography variant="caption" sx={{ 
                      color: card.trendUp ? 'success.main' : 'error.main',
                      fontWeight: 600
                    }}>
                      {card.trend}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: 'text.primary',
                  mb: 0.5
                }}>
                  {animatedValues[card.key] || 0}{card.unit}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  fontWeight: 500
                }}>
                  {card.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Dynamic Chart */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ 
            height: 400,
            background: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #f1f5f9'
          }}>
            <CardContent sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Project Analytics (Real Data)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant={chartType === 'bar' ? 'contained' : 'outlined'}
                    onClick={() => setChartType('bar')}
                  >
                    Bar
                  </Button>
                  <Button
                    size="small"
                    variant={chartType === 'line' ? 'contained' : 'outlined'}
                    onClick={() => setChartType('line')}
                  >
                    Line
                  </Button>
                  <Button
                    size="small"
                    variant={chartType === 'area' ? 'contained' : 'outlined'}
                    onClick={() => setChartType('area')}
                  >
                    Area
                  </Button>
                  <IconButton size="small" onClick={handleChartMenuOpen}>
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ height: '85%', width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  {renderChart()}
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pie Chart */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ 
            height: 400,
            background: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #f1f5f9'
          }}>
            <CardContent sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
                Task Distribution (Real Data)
              </Typography>
              <Box sx={{ height: '70%', width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                {pieData.map((item, index) => (
                  <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      bgcolor: COLORS[index % COLORS.length] 
                    }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {item.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Live Data Section */}
      {isLive && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Card sx={{ 
              background: 'white',
              borderRadius: 3,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              border: '1px solid #f1f5f9'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Live Data Stream (Real-time)
                  </Typography>
                  <Chip label="LIVE" color="error" size="small" />
                </Box>
                <Box sx={{ height: 200, overflow: 'hidden' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={realTimeData}>
                      <XAxis dataKey="time" />
                      <YAxis />
                      <RechartsTooltip />
                      <Area type="monotone" dataKey="value" fill="#3b82f6" stroke="#3b82f6" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Bottom Section */}
      <Grid container spacing={3}>
        {/* Recent Activities */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            background: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #f1f5f9'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Recent Activities (Real Data)
                </Typography>
                <Badge badgeContent={recentActivities.length} color="primary">
                  <NotificationsIcon />
                </Badge>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {recentActivities.map((activity) => (
                  <Box 
                    key={activity.id} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#f8fafc',
                      border: '1px solid #f1f5f9',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: '#e2e8f0',
                        transform: 'translateX(4px)'
                      }
                    }}
                    onClick={() => handleActivityClick(activity)}
                  >
                    <Avatar sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: activity.type === 'proposal' ? '#3b82f6' : 
                               activity.type === 'task' ? '#10b981' :
                               activity.type === 'feature' ? '#f59e0b' : '#8b5cf6'
                    }}>
                      {activity.user.charAt(0)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        {activity.action}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        by {activity.user} • {activity.time}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip 
                        label={activity.type} 
                        size="small"
                        sx={{ 
                          bgcolor: activity.type === 'proposal' ? '#dbeafe' : 
                                   activity.type === 'task' ? '#d1fae5' :
                                   activity.type === 'feature' ? '#fef3c7' : '#ede9fe',
                          color: activity.type === 'proposal' ? '#1e40af' : 
                                 activity.type === 'task' ? '#065f46' :
                                 activity.type === 'feature' ? '#92400e' : '#5b21b6',
                          fontWeight: 500
                        }}
                      />
                      <Chip 
                        label={activity.priority} 
                        size="small"
                        color={activity.priority === 'high' ? 'error' : 
                               activity.priority === 'medium' ? 'warning' : 'default'}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Project Progress */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            background: 'white',
            borderRadius: 3,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #f1f5f9'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
                Project Progress (Real Data)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {projectProgress.map((project, index) => (
                  <Box key={project.id || index}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                        {project.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {project.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={project.progress}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: '#f1f5f9',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          background: project.progress > 70 ? '#10b981' : 
                                     project.progress > 40 ? '#f59e0b' : '#3b82f6'
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {project.status}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Team: {project.team} • Due: {project.deadline}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleChartMenuClose}
      >
        <MenuItem onClick={handleChartMenuClose}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export Data</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleChartMenuClose}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share Chart</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleChartMenuClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Chart Settings</ListItemText>
        </MenuItem>
      </Menu>

      {/* Notifications */}
      <Snackbar
        open={showNotification}
        autoHideDuration={4000}
        onClose={() => setShowNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowNotification(false)} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {notificationMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard; 